import { useState, useEffect } from 'react';
import { ArrowLeft, Save, FileText, Calculator, LayoutTemplate } from 'lucide-react';
import { contratoService, ContratoInput, modeloContratoService, ModeloContrato } from '../../../../services/contratoService';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../../../styles/quill-custom.css';
import { ContratoItens } from './ContratoItens';
import { toLocalISODate } from '../../../../utils/dateUtils';

interface ContratoFormProps {
  contratoId?: string;
  onBack: () => void;
  onSave: () => void;
  clients: Array<{
    id: string;
    razaoSocial: string;
    nomeFantasia?: string | null;
    cnpj: string;
  }>;
}

export function ContratoForm({ contratoId, onBack, onSave, clients }: ContratoFormProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ContratoInput>({
    clientId: '',
    titulo: '',
    dataInicio: toLocalISODate(),
    dataFim: null,
    diaVencimento: 10,
    valorMensal: undefined,
    conteudo: '',
  });
  const [numeroContrato, setNumeroContrato] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [modelos, setModelos] = useState<ModeloContrato[]>([]);
  const [modeloSelecionado, setModeloSelecionado] = useState<string>('');
  const [abaSelecionada, setAbaSelecionada] = useState<'financeiro' | 'documento'>('financeiro');

  const isEditing = !!contratoId;

  const QUILL_MODULES = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['link'],
      ['clean'],
    ],
  };

  const QUILL_FORMATS = [
    'header', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'link',
  ];

  useEffect(() => {
    if (contratoId) {
      carregarContrato();
    } else {
      contratoService.proximoNumero().then(n => setNumeroContrato(n)).catch(() => { });
    }
    carregarModelos();
  }, [contratoId]);

  async function carregarModelos() {
    try {
      const data = await modeloContratoService.list();
      setModelos(data);
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
    }
  }

  async function carregarContrato() {
    try {
      setLoading(true);
      const contrato = await contratoService.getById(contratoId!);
      setForm({
        clientId: contrato.clientId,
        titulo: contrato.titulo,
        dataInicio: contrato.dataInicio.split('T')[0],
        dataFim: contrato.dataFim ? contrato.dataFim.split('T')[0] : null,
        diaVencimento: contrato.diaVencimento,
        valorMensal: contrato.valorMensal != null ? Number(contrato.valorMensal) : null,
        conteudo: contrato.conteudo || '',
      });
      if (contrato.numero) setNumeroContrato(contrato.numero);
    } catch (error) {
      console.error('Erro ao carregar contrato:', error);
      alert('Erro ao carregar contrato');
    } finally {
      setLoading(false);
    }
  }

  function aplicarTemplate() {
    if (!modeloSelecionado) return;
    
    const modelo = modelos.find(m => m.id === modeloSelecionado);
    if (!modelo) return;

    if (form.conteudo.trim() && !window.confirm('Substituir o conteúdo atual pelo template selecionado?')) {
      return;
    }

    // Pegar o conteúdo do template
    let conteudoProcessado = modelo.conteudo;

    // Se temos cliente e dados do contrato, aplicar substituição automática UMA VEZ
    if (form.clientId) {
      const cliente = clients.find(c => c.id === form.clientId);
      if (cliente) {
        // Funções auxiliares com proteção total
        function formatarDataCorreta(dataString: string | null | undefined): string {
          try {
            if (!dataString) return 'Indeterminado';
            const [ano, mes, dia] = dataString.split('-');
            if (!ano || !mes || !dia) return 'Indeterminado';
            const data = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
            return data.toLocaleDateString('pt-BR');
          } catch (error) {
            console.error('Erro ao formatar data:', error);
            return 'Indeterminado';
          }
        }

        function formatarCNPJ(cnpj: string | null | undefined): string {
          try {
            if (!cnpj) return '[CNPJ]';
            const numeros = cnpj.replace(/\D/g, '');
            if (numeros.length !== 14) return cnpj;
            return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
          } catch (error) {
            console.error('Erro ao formatar CNPJ:', error);
            return '[CNPJ]';
          }
        }

        function formatarValor(valor: number | null | undefined): string {
          try {
            if (valor == null || isNaN(valor)) return 'A definir';
            return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
          } catch (error) {
            console.error('Erro ao formatar valor:', error);
            return 'A definir';
          }
        }

        // Substituir variáveis do template
        const variaveis: Record<string, string> = {
          '{{CLIENTE_NOME}}': cliente.nomeFantasia || cliente.razaoSocial || '[CLIENTE]',
          '{{CLIENTE_CNPJ}}': formatarCNPJ(cliente.cnpj),
          '{{EMPRESA_NOME}}': 'CODDAR DIGITAL',
          '{{CIDADE_EMPRESA}}': 'Santos/SP',
          '{{CONTRATO_NUMERO}}': numeroContrato || '[NÚMERO]',
          '{{VALOR}}': formatarValor(form.valorMensal),
          '{{DATA_HOJE}}': new Date().toLocaleDateString('pt-BR'),
          '{{DATA_INICIO}}': formatarDataCorreta(form.dataInicio),
          '{{DATA_FIM}}': formatarDataCorreta(form.dataFim),
          '{{DIA_VENCIMENTO}}': String(form.diaVencimento || 10),
        };

        for (const [variavel, valor] of Object.entries(variaveis)) {
          if (variavel && valor !== undefined && valor !== null) {
            conteudoProcessado = conteudoProcessado.split(variavel).join(valor);
          }
        }
      }
    }

    // Atualizar o conteúdo no formulário
    setForm({ ...form, conteudo: conteudoProcessado });
    
    // Mudar para a aba de documento
    setAbaSelecionada('documento');
  }

  function validar(): boolean {
    const novosErros: Record<string, string> = {};

    if (!form.clientId) {
      novosErros.clientId = 'Selecione um cliente';
    }
    if (!form.titulo.trim()) {
      novosErros.titulo = 'Título é obrigatório';
    }
    if (!form.dataInicio) {
      novosErros.dataInicio = 'Data de início é obrigatória';
    }
    if (form.diaVencimento < 1 || form.diaVencimento > 28) {
      novosErros.diaVencimento = 'Dia de vencimento deve ser entre 1 e 28';
    }

    setErrors(novosErros);
    return Object.keys(novosErros).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validar()) return;

    try {
      setSaving(true);

      if (isEditing) {
        await contratoService.update(contratoId!, form);
      } else {
        await contratoService.create(form);
      }

      onSave();
    } catch (error: any) {
      console.error('Erro ao salvar contrato:', error);
      alert(error.response?.data?.error || 'Erro ao salvar contrato');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-muted rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            {isEditing ? 'Editar Contrato' : 'Novo Contrato'}
          </h2>
          <p className="text-muted-foreground">
            {isEditing ? 'Atualize as informações do contrato' : 'Crie um novo contrato recorrente'}
          </p>
        </div>
        {numeroContrato && (
          <span className="ml-auto px-3 py-1.5 rounded-lg bg-muted border border-border text-sm font-mono text-muted-foreground select-none" title="Número gerado automaticamente pelo sistema">
            Nº {numeroContrato}
          </span>
        )}
      </div>

      {/* Abas */}
      <div className="flex border-b border-border">
        <button
          type="button"
          onClick={() => setAbaSelecionada('financeiro')}
          className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors ${
            abaSelecionada === 'financeiro'
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Calculator className="w-4 h-4" />
          Motor Financeiro
        </button>
        <button
          type="button"
          onClick={() => setAbaSelecionada('documento')}
          className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors ${
            abaSelecionada === 'documento'
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <FileText className="w-4 h-4" />
          Documento Jurídico
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ABA: Motor Financeiro */}
        {abaSelecionada === 'financeiro' && (
          <div className="space-y-6">
            {/* Configuração Financeira Básica */}
            <div className="bg-card rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Configuração Financeira
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Cliente *
                  </label>
                  <select
                    value={form.clientId || ''}
                    onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground bg-card ${errors.clientId ? 'border-red-500' : ''}`}
                  >
                    <option value="">Selecione um cliente</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.nomeFantasia || client.razaoSocial}
                      </option>
                    ))}
                  </select>
                  {errors.clientId && <p className="text-red-500 text-xs mt-1">{errors.clientId}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Título *
                  </label>
                  <input
                    type="text"
                    value={form.titulo}
                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground bg-card ${errors.titulo ? 'border-red-500' : ''}`}
                    placeholder="Ex: Contrato de Manutenção Mensal"
                  />
                  {errors.titulo && <p className="text-red-500 text-xs mt-1">{errors.titulo}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Data de Início *
                  </label>
                  <input
                    type="date"
                    value={form.dataInicio}
                    onChange={(e) => setForm({ ...form, dataInicio: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground bg-card ${errors.dataInicio ? 'border-red-500' : ''}`}
                  />
                  {errors.dataInicio && <p className="text-red-500 text-xs mt-1">{errors.dataInicio}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Data de Fim (opcional)
                  </label>
                  <input
                    type="date"
                    value={form.dataFim || ''}
                    onChange={(e) => setForm({ ...form, dataFim: e.target.value || null })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground bg-card"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Deixe em branco para contrato por tempo indeterminado</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Valor Mensal (R$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.valorMensal ?? ''}
                    onChange={(e) => setForm({ ...form, valorMensal: e.target.value ? parseFloat(e.target.value) : null })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground bg-card"
                    placeholder="Deixe em branco se não aplicável"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Opcional — deixe em branco para contratos sem valor fixo</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Dia de Vencimento *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="28"
                    value={form.diaVencimento}
                    onChange={(e) => setForm({ ...form, diaVencimento: parseInt(e.target.value) || 10 })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground bg-card ${errors.diaVencimento ? 'border-red-500' : ''}`}
                  />
                  {errors.diaVencimento && <p className="text-red-500 text-xs mt-1">{errors.diaVencimento}</p>}
                  <p className="text-xs text-muted-foreground mt-1">Dia do mês para vencimento das faturas (1-28)</p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  💡 <strong>Dica:</strong> Configure primeiro os dados financeiros, depois adicione os serviços do catálogo abaixo e, por fim, passe para a aba "Documento Jurídico" para aplicar um template.
                </p>
              </div>
            </div>

            {/* Itens do Contrato (Catálogo de Serviços) - Só aparece em modo de edição */}
            {isEditing && contratoId && (
              <ContratoItens contratoId={contratoId} />
            )}

            {/* Aviso para contratos novos */}
            {!isEditing && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  ℹ️ <strong>Importante:</strong> Salve o contrato primeiro para poder adicionar os serviços do catálogo (ex: Gestão Integrada Cloud).
                </p>
              </div>
            )}
          </div>
        )}

        {/* ABA: Documento Jurídico */}
        {abaSelecionada === 'documento' && (
          <div className="space-y-6">
            {/* Seleção de Template */}
            <div className="bg-card rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                <LayoutTemplate className="w-5 h-5" />
                Aplicar Template
              </h3>

              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Selecione um template
                  </label>
                  <select
                    value={modeloSelecionado || ''}
                    onChange={(e) => setModeloSelecionado(e.target.value || '')}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground bg-card"
                  >
                    <option value="">Escolha um template...</option>
                    {modelos.map(modelo => (
                      <option key={modelo.id} value={modelo.id}>
                        {modelo.titulo} {modelo.tipoNegocio && `(${modelo.tipoNegocio})`}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={aplicarTemplate}
                  disabled={!modeloSelecionado}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <LayoutTemplate className="w-4 h-4" />
                  Aplicar
                </button>
              </div>

              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                  <strong>💡 Como funciona:</strong> Ao clicar em "Aplicar", o template será carregado com as variáveis preenchidas automaticamente.
                </p>
                <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                  <p><strong>📝 Edição Livre:</strong> Após aplicar o template, você pode editar o texto livremente no editor.</p>
                  <p><strong>⚠️ Importante:</strong> As variáveis são substituídas apenas UMA VEZ ao aplicar o template.</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-blue-700 dark:text-blue-300 mt-2">
                  <div><code>{'{{CLIENTE_NOME}}'}</code> → Nome do cliente</div>
                  <div><code>{'{{CLIENTE_CNPJ}}'}</code> → CNPJ do cliente</div>
                  <div><code>{'{{DATA_INICIO}}'}</code> → Data de início</div>
                  <div><code>{'{{DATA_FIM}}'}</code> → Data de fim</div>
                  <div><code>{'{{VALOR}}'}</code> → Valor mensal</div>
                  <div><code>{'{{DIA_VENCIMENTO}}'}</code> → Dia vencimento</div>
                  <div><code>{'{{DATA_HOJE}}'}</code> → Data atual</div>
                  <div><code>{'{{CIDADE_EMPRESA}}'}</code> → Santos/SP</div>
                  <div><code>{'{{EMPRESA_NOME}}'}</code> → CODDAR DIGITAL</div>
                </div>
              </div>
            </div>

            {/* Editor de Conteúdo */}
            <div className="bg-card rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Conteúdo do Contrato
              </h3>

              <div className="h-[500px]">
                <ReactQuill
                  theme="snow"
                  value={form.conteudo || ''}
                  onChange={(conteudo) => setForm({ ...form, conteudo: conteudo || '' })}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  className="quill-contract-editor h-[450px]"
                  placeholder="Digite o conteúdo do contrato aqui ou aplique um template acima..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Ações */}
        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-foreground hover:bg-muted rounded-lg"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
}
